import React, { useCallback } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import CheckIcon from 'components/material-symbol/icons/Check'
import { useHistory } from 'react-router-dom'
import { useFormContext } from 'react-hook-form'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import FormSectionContainer from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-section-container'
import EditEventPagination from 'pages/pga-coach/bookings/events/edit/edit-event-form/edit-event-pagination'
import CreateProfileSectionNav from 'pages/pga-coach/profile/new/create-profile-form/create-profile-section-nav'
import TipWell from 'components/tip-well'
import InfoIcon from 'components/material-symbol/icons/Info'
import PhysicalInformation from './physical-information'
import GolfInformation from './golf-information'
import GolfFocusInformation from './golf-focus-information'
import {
  GOLFER_INTAKE_SECTIONS,
  getGolferIntakeSectionIndex,
} from './golfer-intake-sections'

const Layout = ({ activeStep, setActiveStep }) => {
  const classes = useStyles()
  const history = useHistory()
  const { trigger, formState } = useFormContext()
  const { isDirty, isSubmitting } = formState

  const sections = GOLFER_INTAKE_SECTIONS
  const activeSection = sections[activeStep]?.id
  const isFirstStep = activeStep === 0
  const isLastStep = activeStep === sections.length - 1
  const currentSection = sections[activeStep]

  const backLinkUrl = window.location.pathname
    .split('/')
    .slice(0, -2)
    .join('/')

  const scrollToFormTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleCancel = useCallback(() => {
    history.push(backLinkUrl)
  }, [backLinkUrl, history])

  const handleSectionClick = useCallback(
    sectionId => {
      setActiveStep(getGolferIntakeSectionIndex(sectionId))
      scrollToFormTop()
    },
    [setActiveStep, scrollToFormTop],
  )

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setActiveStep(activeStep - 1)
      scrollToFormTop()
    }
  }, [activeStep, isFirstStep, scrollToFormTop, setActiveStep])

  const handleNext = useCallback(async () => {
    if (isLastStep) return

    const fields = currentSection?.fields ?? []
    const isValid = fields.length ? await trigger(fields) : true

    if (isValid) {
      setActiveStep(activeStep + 1)
      scrollToFormTop()
    }
  }, [
    activeStep,
    currentSection,
    isLastStep,
    scrollToFormTop,
    setActiveStep,
    trigger,
  ])

  return (
    <>
      <Box className={classes.actionBar}>
        <Button
          type="button"
          className={classes.cancelAction}
          startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.saveAction}
          disabled={!isDirty || isSubmitting}
          startIcon={<CheckIcon fontSize="small" />}
          id="complete-button"
        >
          Save profile
        </Button>
      </Box>

      <Container maxWidth="lg">
        <Box pt={4} pb={3}>
          <Box className={classes.layoutGrid}>
            <Box className={classes.navColumn}>
              <CreateProfileSectionNav
                sections={sections}
                activeSection={activeSection}
                onSectionClick={handleSectionClick}
              />
            </Box>

            <Box className={classes.formColumn}>
              <Box display={activeStep === 0 ? 'block' : 'none'}>
                <Box mb={3}>
                  <TipWell
                    icon={InfoIcon}
                    caption="These questions are optional. Share what you can so your coach can personalize your lessons."
                  />
                </Box>
                <FormSectionContainer
                  id={sections[0].id}
                  title={sections[0].pageTitle}
                >
                  <GolfInformation />
                  <Box mt={3}>
                    <GolfFocusInformation />
                  </Box>
                </FormSectionContainer>
              </Box>
              <Box display={activeStep === 1 ? 'block' : 'none'}>
                <FormSectionContainer
                  id={sections[1].id}
                  title={sections[1].pageTitle}
                >
                  <PhysicalInformation />
                </FormSectionContainer>
              </Box>
              <EditEventPagination
                isFirstStep={isFirstStep}
                isLastStep={isLastStep}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  )
}

export default Layout
